import { useCallback, useState } from 'react';

/**
 * Hook for persisting state in localStorage with React state synchronization.
 *
 * @param key - Unique key for localStorage
 * @param defaultValue - Default value if no stored value exists
 * @returns Tuple of [value, setValue] similar to useState
 *
 * @example
 * const [isOpen, setIsOpen] = useLocalStorage('sidebar_open', true);
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize state with stored value or default
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? (JSON.parse(item) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  // Memoized setter that updates both state and localStorage
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const newValue = value instanceof Function ? value(prev) : value;

        if (typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(key, JSON.stringify(newValue));
          } catch {
            // Silently fail if localStorage is not available (e.g., quota exceeded)
          }
        }

        return newValue;
      });
    },
    [key],
  );

  return [storedValue, setValue];
}
